// White-box test surface. The deterministic oracle (Playwright + fast-check) reads
// and drives the engine ONLY through this. See research/DECISIONS.md #4 + cross-cutting #3.
import type { Vec2 } from "../core/geometry";

export interface TestState {
  roomId: string;
  step: number;
  seed: number;
  actor: { x: number; y: number; moving: boolean; facing: number };
  target: Vec2 | null;
  targetKind: "walk" | "hotspot" | "exit" | "npc" | null;
  clamped: boolean;
  lastClick: Vec2 | null;
  lastInteraction: { kind: string; id: string; verb: string; step: number } | null;
  /** the interactable currently primed for the interact button (proximity + facing) */
  armed: { kind: "hotspot" | "npc"; id: string; verb: string } | null;
  /** live room puzzle state (flags/phases) — resets on room entry */
  state: Record<string, boolean | number | string>;
  /** live GLOBAL state (saga/quest flags) — persists across rooms */
  globalState: Record<string, boolean | number | string>;
  /** the carried flame's current level (0 = unlit) */
  flame: number;
  /** whether the quest win condition currently holds */
  won: boolean;
  /** ids of gated areas whose condition currently holds (folded into the navmesh) */
  openAreas: string[];
  /** the open dialogue line, if any (text-first; glyphs optional/decorative) */
  dialog: { npcId: string; index: number; total: number; text: string | null; glyphs: string[] | null; mood: string } | null;
  /** the prose currently shown on screen (dialogue line, examine line, or room card), else null */
  caption: string | null;
  /** the current room's title (for the entry card), if any */
  roomName: string | null;
  fade: string;
  depthOrder: string[]; // entity ids, back-to-front (paint order)
  npcs: { id: string; x: number; y: number }[];
  hover: { kind: string; id: string | null };
  camera: { x: number; y: number };
  view: { w: number; h: number };
  room: { w: number; h: number };
  clickCount: number;
}

export interface TestApi {
  version: number;
  state: () => TestState;
  /** inject a click in ROOM-SPACE (bypasses pointer transform) — for replay */
  click: (x: number, y: number) => void;
  /** update hover in ROOM-SPACE (drives cursor + affordance) */
  hover: (x: number, y: number) => void;
  /** fire the context-sensitive interact button on the currently armed target (if any) */
  interact: () => void;
  /** jump directly to a room at its spawn (dev/test convenience) */
  goto: (roomId: string) => void;
  /** the current room's full validated data (size/walkmesh/decor/hotspots/...) — for art tooling */
  room: () => unknown;
  /** advance N exact fixed steps deterministically (headless replay) */
  tick: (n: number) => void;
  /** pause/resume the real-time loop so tick() is the sole clock (deterministic tests) */
  pause: () => void;
  resume: () => void;
  /** reset simulation to a clean deterministic origin (step 0, actor at spawn), and pause */
  reset: () => void;
  /** event log for golden replay */
  log: () => ReadonlyArray<{ step: number; x: number; y: number; kind: string }>;
}

declare global {
  interface Window {
    __TEST__?: TestApi;
  }
}

export function installProbe(api: TestApi): void {
  window.__TEST__ = api;
}
